import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Offset-based infinite scroll.
 *  - `fetchPage(offset, signal)` resolves to an array of items for that page.
 *  - `resetKey` (string) — when it changes, the list resets and reloads from offset 0.
 *  - `pageSize` — a page that returns fewer than this is treated as the last page.
 * Returns the accumulated items plus a `sentinelRef` to place at the bottom of the list;
 * when it scrolls into view the next page auto-loads.
 */
export function useInfiniteScroll({ fetchPage, resetKey, pageSize = 24 }) {
  const [items, setItems] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  // Initial load + reset whenever the key (search/filters/etc.) changes.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    offsetRef.current = 0;
    loadingRef.current = true;
    setItems(null);
    setHasMore(false);
    (async () => {
      try {
        const page = await fetchRef.current(0, controller.signal);
        if (cancelled) return;
        setItems(page);
        offsetRef.current = page.length;
        setHasMore(page.length >= pageSize);
      } catch (e) {
        if (!cancelled && e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          setItems([]); setHasMore(false);
        }
      } finally {
        if (!cancelled) loadingRef.current = false;
      }
    })();
    return () => { cancelled = true; controller.abort(); loadingRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, pageSize]);

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoadingMore(true);
    try {
      const page = await fetchRef.current(offsetRef.current);
      setItems((prev) => [...(prev || []), ...page]);
      offsetRef.current += page.length;
      setHasMore(page.length >= pageSize);
    } catch (e) {
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [pageSize]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingRef.current) loadMore();
    }, { rootMargin: "500px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadMore, items]);

  return { items, loadingMore, hasMore, sentinelRef };
}
